/**
 * Settings Filters - AJAX Field Handler
 * Handles automatic saving of filter settings when fields are changed
 * 
 * @author InterPals Development Team
 * @version 1.0.0
 */

(function() {
    'use strict';

    // Constants
    const FEEDBACK_DURATION = {
        SUCCESS: 1200,
        ERROR: 2500
    };

    const REQUEST_TIMEOUT = 10000; // 10 seconds
    const DEBOUNCE_DELAY = 500; // For slider

    const CSS_CLASSES = {
        HIDDEN: 'hidden',
        OPACITY_70: 'opacity-70',
        SUCCESS_FEEDBACK: 'transition-all duration-300 bg-green-50',
        ERROR_FEEDBACK: 'transition-all duration-300 bg-red-50',
        CONTINENT_CHIP: 'continent-chip',
        COUNTRY_TAG: 'country-tag',
        DISABLED: 'disabled',
        POINTER_EVENTS_NONE: 'pointer-events-none'
    };
    
    // Unified endpoint for all settings field updates
    const FIELD_UPDATE_URL = '/app/settings/field/update';

    const SELECTORS = {
        CSRF_TOKEN_META: 'meta[name="csrf_token"]',
        CSRF_TOKEN_INPUT: 'input[name="csrf_token"]',
        AGE_SLIDER_MIN: 'input[name="contact_rules_age1"]',
        AGE_SLIDER_MAX: 'input[name="contact_rules_age2"]',
        SEX_SELECT: '.inline-select-container[data-name="contact_rules_sex"]',
        CONTINENT_CHIPS_CONTAINER: '[data-field="blocked_continents"]',
        COUNTRY_SELECTOR: '#blocked-countries-selector'
    };

    let ageSliderTimeout = null;

    /**
     * Get CSRF token from meta tag or hidden input
     * @returns {string|null} CSRF token value or null if not found
     */
    function getCsrfToken() {
        const csrfTokenMeta = document.querySelector(SELECTORS.CSRF_TOKEN_META);
        const csrfTokenInput = document.querySelector(SELECTORS.CSRF_TOKEN_INPUT);

        if (csrfTokenMeta) {
            return csrfTokenMeta.getAttribute('content');
        } else if (csrfTokenInput) {
            return csrfTokenInput.value;
        }
        
        return null;
    }

    /**
     * Apply visual feedback to an element
     * @param {HTMLElement} element - The element
     * @param {string} type - 'success' or 'error'
     * @param {number} duration - Duration in milliseconds
     */
    function applyVisualFeedback(element, type, duration) {
        if (!element) return;

        // Remove opacity immediately
        element.classList.remove(CSS_CLASSES.OPACITY_70);

        if (type === 'success') {
            // Show a floating checkmark indicator
            showSaveIndicator(element, true);
        } else if (type === 'error') {
            // Show error state briefly
            element.classList.add('bg-red-50');
            setTimeout(function() {
                element.classList.remove('bg-red-50');
            }, duration);
        }
    }

    /**
     * Show a save indicator below the slider
     * @param {HTMLElement} element - The element to attach to
     * @param {boolean} success - Whether it was successful
     */
    function showSaveIndicator(element, success) {
        // Remove any existing indicator
        const existingIndicator = element.querySelector('.save-indicator');
        if (existingIndicator) {
            existingIndicator.remove();
        }

        // Create the indicator
        const indicator = document.createElement('div');
        indicator.className = 'save-indicator';
        indicator.innerHTML = success
            ? '<svg class="inline-block w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path></svg>Saved'
            : '<svg class="inline-block w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>Error';

        // Style it - position below the slider, left aligned
        Object.assign(indicator.style, {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            marginTop: '8px',
            color: success ? '#10b981' : '#ef4444',
            fontSize: '13px',
            fontWeight: '500',
            animation: 'fadeIn 0.2s ease-out'
        });

        // Add animation keyframes if not already present
        if (!document.getElementById('save-indicator-styles')) {
            const style = document.createElement('style');
            style.id = 'save-indicator-styles';
            style.textContent = `
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes fadeOut {
                    from { opacity: 1; }
                    to { opacity: 0; }
                }
            `;
            document.head.appendChild(style);
        }

        element.appendChild(indicator);

        // Remove after delay
        setTimeout(function() {
            indicator.style.animation = 'fadeOut 0.3s ease-out forwards';
            setTimeout(function() {
                indicator.remove();
            }, 300);
        }, 1200);
    }

    /**
     * Send field update to server
     * @param {string} fieldName - Field name
     * @param {string} fieldValue - Field value
     * @param {HTMLElement} container - Container element for visual feedback
     * @param {Function} onError - Error callback
     */
    function sendFieldUpdate(fieldName, fieldValue, container, onError) {
        // Add visual loading state
        if (container) {
            container.classList.add(CSS_CLASSES.OPACITY_70);
        }

        // Prepare form data
        const formData = new FormData();
        formData.append('field', fieldName);
        formData.append('value', fieldValue);

        // Add CSRF token
        const tokenValue = getCsrfToken();
        if (tokenValue) {
            formData.append('csrf_token', tokenValue);
        }

        // Make AJAX request
        fetch(FIELD_UPDATE_URL, {
            method: 'POST',
            body: formData,
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            }
        })
        .then(function(response) {
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            return response.json();
        })
        .then(function(data) {
            // Always remove loading state first
            if (container) {
                container.classList.remove(CSS_CLASSES.OPACITY_70);
            }
            
            if (data.success) {
                // Show success feedback
                applyVisualFeedback(container, 'success', FEEDBACK_DURATION.SUCCESS);
            } else {
                throw new Error(data.error_msg || 'Unknown error occurred');
            }
        })
        .catch(function(error) {
            // Always remove loading state first
            if (container) {
                container.classList.remove(CSS_CLASSES.OPACITY_70);
            }
            
            // Show error feedback
            applyVisualFeedback(container, 'error', FEEDBACK_DURATION.ERROR);
            if (onError) {
                onError(error);
            }
        });
    }

    /**
     * Handle age slider changes
     */
    function handleAgeSliderChange(values) {
        // Clear existing timeout
        if (ageSliderTimeout) {
            clearTimeout(ageSliderTimeout);
        }

        // Debounce the save
        ageSliderTimeout = setTimeout(function() {
            let minAge, maxAge;

            if (values && values.length === 2) {
                // jQuery UI slider provides values array
                minAge = values[0];
                maxAge = values[1];
            } else {
                // Fallback to reading from hidden inputs
                const minSlider = document.querySelector(SELECTORS.AGE_SLIDER_MIN);
                const maxSlider = document.querySelector(SELECTORS.AGE_SLIDER_MAX);
                if (!minSlider || !maxSlider) return;
                minAge = minSlider.value;
                maxAge = maxSlider.value;
            }

            // Convert 65 to 110 for storage (slider displays "65+" but stores 110)
            if (parseInt(maxAge) === 65) {
                maxAge = 110;
            }

            const ageRange = minAge + ',' + maxAge;
            const container = document.querySelector('.double-range-slider-container');
            if (container) {
                // Use the slider container itself for more specific loading state
                sendFieldUpdate('contact_rules_age', ageRange, container);
            }
        }, DEBOUNCE_DELAY);
    }

    /**
     * Initialize age sliders
     */
    function initAgeSliders() {
        // Wait for jQuery UI to be ready and then watch for changes to the hidden inputs
        setTimeout(function() {
            const minInput = document.querySelector(SELECTORS.AGE_SLIDER_MIN);
            const maxInput = document.querySelector(SELECTORS.AGE_SLIDER_MAX);
            
            if (!minInput || !maxInput) {
                // Age sliders only exist on settings/filters page - silently skip on other pages
                return;
            }
            
            // Watch for value changes on the hidden inputs
            // The jQuery UI slider updates these inputs when sliding
            let lastMinValue = minInput.value;
            let lastMaxValue = maxInput.value;
            
            // Use MutationObserver to detect value changes
            const observer = new MutationObserver(function(mutations) {
                const currentMinValue = minInput.value;
                const currentMaxValue = maxInput.value;
                
                if (currentMinValue !== lastMinValue || currentMaxValue !== lastMaxValue) {
                    lastMinValue = currentMinValue;
                    lastMaxValue = currentMaxValue;
                    handleAgeSliderChange();
                }
            });
            
            // Observe changes to the value attribute
            observer.observe(minInput, { attributes: true, attributeFilter: ['value'] });
            observer.observe(maxInput, { attributes: true, attributeFilter: ['value'] });
            
            // Also listen for the jQuery UI slider if available
            const sliderElement = document.getElementById('contact_rules_age1_contact_rules_age2_slider');
            if (sliderElement && typeof $ !== 'undefined' && $.fn.slider) {
                $(sliderElement).on('slidestop', function(event, ui) {
                    // Force a check when sliding stops
                    handleAgeSliderChange(ui.values);
                });
            }
            
            // Fallback: poll for changes every 500ms while user might be interacting
            let pollInterval = null;
            const startPolling = function() {
                if (!pollInterval) {
                    pollInterval = setInterval(function() {
                        const currentMinValue = minInput.value;
                        const currentMaxValue = maxInput.value;
                        
                        if (currentMinValue !== lastMinValue || currentMaxValue !== lastMaxValue) {
                            lastMinValue = currentMinValue;
                            lastMaxValue = currentMaxValue;
                            handleAgeSliderChange();
                        }
                    }, 500);
                }
            };
            
            const stopPolling = function() {
                if (pollInterval) {
                    clearInterval(pollInterval);
                    pollInterval = null;
                }
            };
            
            // Start polling on mousedown/touchstart on the slider container
            const sliderContainer = document.querySelector('.double-range-slider-container');
            if (sliderContainer) {
                sliderContainer.addEventListener('mousedown', startPolling);
                sliderContainer.addEventListener('touchstart', startPolling);
                
                // Stop polling on mouseup/touchend
                document.addEventListener('mouseup', stopPolling);
                document.addEventListener('touchend', stopPolling);
            }
        }, 500); // Give jQuery UI time to initialize
    }

    /**
     * Initialize contact rules sex field
     */
    function initContactRulesSex() {
        // Listen for inline select change events
        const sexContainer = document.querySelector(SELECTORS.SEX_SELECT);
        if (sexContainer) {
            sexContainer.addEventListener('inlineSelectChange', function(event) {
                const hiddenInput = sexContainer.querySelector('input[type="hidden"]');
                if (hiddenInput) {
                    // Use the sex container itself for more specific loading state
                    sendFieldUpdate('contact_rules_sex', hiddenInput.value, sexContainer);
                }
            });
        }
    }

    /**
     * Get current continent values
     */
    function getCurrentContinents() {
        const checkboxes = document.querySelectorAll('.continent-chip input[type="checkbox"]:checked');
        const continents = [];
        checkboxes.forEach(function(checkbox) {
            if (checkbox.value) {
                continents.push(checkbox.value);
            }
        });
        return continents.join(',');
    }

    /**
     * Initialize continent chips
     */
    function initContinentChips() {
        // Use a slight delay to ensure multi-select.js has initialized first
        setTimeout(function() {
            const chips = document.querySelectorAll('.' + CSS_CLASSES.CONTINENT_CHIP);
            chips.forEach(function(chip) {
                // The chips are labels, so we need to handle the checkbox change instead
                const checkbox = chip.querySelector('input[type="checkbox"]');
                if (!checkbox) return;
                
                // Listen for checkbox change events
                checkbox.addEventListener('change', function() {
                    // Get the continent chips wrapper specifically for loading state
                    const chipsWrapper = chip.closest('.continent-chips-wrapper');
                    if (!chipsWrapper) {
                        // Fallback to the data-field container
                        const container = chip.closest('[data-field="blocked_continents"]');
                        if (container) {
                            sendFieldUpdate('blocked_continents', getCurrentContinents(), container);
                        }
                    } else {
                        // Send update after a small delay to ensure the checkbox state is updated
                        setTimeout(function() {
                            const continents = getCurrentContinents();
                            sendFieldUpdate('blocked_continents', continents, chipsWrapper);
                        }, 10);
                    }
                });
            });
        }, 100);
    }

    /**
     * Get current country values
     */
    function getCurrentCountries() {
        const countryTags = document.querySelectorAll('.' + CSS_CLASSES.COUNTRY_TAG);
        const countries = [];
        countryTags.forEach(function(tag) {
            const countryCode = tag.getAttribute('data-country');
            if (countryCode) {
                countries.push(countryCode);
            }
        });
        return countries.join(',');
    }

    /**
     * Initialize country selector
     */
    function initCountrySelector() {
        // Listen for custom events from the country selector widget
        document.addEventListener('countryAdded', function(event) {
            const container = document.querySelector(SELECTORS.COUNTRY_SELECTOR);
            if (container) {
                // Find the chips container within for more specific loading state
                const chipsContainer = container.querySelector('.selected-countries-chips');
                const targetContainer = chipsContainer || container;
                const countries = getCurrentCountries();
                sendFieldUpdate('blocked_countries', countries, targetContainer);
            }
        });
        
        document.addEventListener('countryRemoved', function(event) {
            const container = document.querySelector(SELECTORS.COUNTRY_SELECTOR);
            if (container) {
                // Find the chips container within for more specific loading state
                const chipsContainer = container.querySelector('.selected-countries-chips');
                const targetContainer = chipsContainer || container;
                const countries = getCurrentCountries();
                sendFieldUpdate('blocked_countries', countries, targetContainer);
            }
        });
    }

    /**
     * Show inline error message
     * @param {HTMLElement} container - The container element
     * @param {string} message - Error message to display
     */
    function showInlineError(container, message) {
        // Remove any existing error messages
        removeInlineErrors(container);
        
        // Create error element
        const errorDiv = document.createElement('div');
        errorDiv.className = 'inline-error text-xs text-red-600 mt-2 flex items-center gap-1';
        errorDiv.innerHTML = '<i class="fa fa-exclamation-circle"></i> ' + message;
        
        // Insert after the container
        container.appendChild(errorDiv);
        
        // Auto-remove after 5 seconds
        setTimeout(function() {
            removeInlineErrors(container);
        }, 5000);
    }
    
    /**
     * Remove inline error messages
     * @param {HTMLElement} container - The container element
     */
    function removeInlineErrors(container) {
        const errors = container.querySelectorAll('.inline-error');
        errors.forEach(function(error) {
            error.remove();
        });
    }


    /**
     * Initialize block list handlers (new chip-based system)
     */
    function initBlockList() {
        const addInput = document.getElementById('add-blocked-user-input');
        const addBtn = document.getElementById('add-blocked-user-btn');
        const chipsContainer = document.getElementById('blocked-users-chips');
        const statusDiv = document.getElementById('block-list-status');
        const errorDiv = document.getElementById('block-user-error');
        const blockedCountSpan = document.getElementById('blocked-count');
        
        if (!addInput || !addBtn) {
            return;
        }
        
        // Enable/disable add button based on input
        addInput.addEventListener('input', function() {
            const hasValue = this.value.trim().length > 0;
            addBtn.disabled = !hasValue;
            // Clear error when typing
            if (errorDiv) {
                errorDiv.classList.add('hidden');
                errorDiv.textContent = '';
            }
        });
        
        // Handle Enter key in input
        addInput.addEventListener('keypress', function(e) {
            if (e.key === 'Enter' && !addBtn.disabled) {
                e.preventDefault();
                handleBlockUser();
            }
        });
        
        // Handle add button click
        addBtn.addEventListener('click', handleBlockUser);
        
        // Handle unblock buttons
        document.addEventListener('click', function(e) {
            if (e.target.closest('.unblock-user-btn')) {
                const btn = e.target.closest('.unblock-user-btn');
                const username = btn.dataset.username;
                if (username) {
                    handleUnblockUser(username);
                }
            }
        });
        
        /**
         * Handle blocking a user
         */
        function handleBlockUser() {
            const username = addInput.value.trim();
            if (!username) return;
            
            // Disable controls during request
            addInput.disabled = true;
            addBtn.disabled = true;
            
            // Show loading state
            showStatus('Blocking user...', 'info');
            
            // Prepare form data
            const formData = new FormData();
            formData.append('field', 'block_user');
            formData.append('username', username);
            
            // Add CSRF token
            const tokenValue = getCsrfToken();
            if (tokenValue) {
                formData.append('csrf_token', tokenValue);
            }
            
            // Make AJAX request
            fetch(FIELD_UPDATE_URL, {
                method: 'POST',
                body: formData,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                }
            })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
                return response.json();
            })
            .then(data => {
                if (data.success) {
                    // Add chip to UI
                    addUserChip(data.user.username);
                    
                    // Clear input
                    addInput.value = '';
                    
                    // Update count
                    updateBlockedCount(1);
                    
                    // Show success
                    showStatus('User blocked successfully', 'success');
                } else {
                    // Show error
                    showError(data.error_msg || 'Failed to block user');
                }
            })
            .catch(error => {
                showError('Network error. Please try again.');
            })
            .finally(() => {
                // Re-enable controls
                addInput.disabled = false;
                addBtn.disabled = false;
                addInput.focus();
            });
        }
        
        /**
         * Handle unblocking a user
         */
        function handleUnblockUser(username) {
            const chip = document.querySelector(`.blocked-user-chip[data-username="${username}"]`);
            if (!chip) return;
            
            // Show loading state on chip
            chip.classList.add('opacity-50');
            const btn = chip.querySelector('.unblock-user-btn');
            if (btn) btn.disabled = true;
            
            showStatus('Unblocking user...', 'info');
            
            // Prepare form data
            const formData = new FormData();
            formData.append('field', 'unblock_user');
            formData.append('username', username);
            
            // Add CSRF token
            const tokenValue = getCsrfToken();
            if (tokenValue) {
                formData.append('csrf_token', tokenValue);
            }
            
            // Make AJAX request
            fetch(FIELD_UPDATE_URL, {
                method: 'POST',
                body: formData,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                }
            })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
                return response.json();
            })
            .then(data => {
                if (data.success) {
                    // Remove chip with animation
                    chip.style.transition = 'all 0.3s ease-out';
                    chip.style.transform = 'scale(0.8)';
                    chip.style.opacity = '0';
                    
                    setTimeout(() => {
                        chip.remove();
                        checkEmptyState();
                    }, 300);
                    
                    // Update count
                    updateBlockedCount(-1);
                    
                    // Show success
                    showStatus('User unblocked successfully', 'success');
                } else {
                    // Restore chip state
                    chip.classList.remove('opacity-50');
                    if (btn) btn.disabled = false;
                    
                    showError(data.error_msg || 'Failed to unblock user');
                }
            })
            .catch(error => {
                // Restore chip state
                chip.classList.remove('opacity-50');
                if (btn) btn.disabled = false;
                
                showError('Network error. Please try again.');
            });
        }
        
        /**
         * Add a user chip to the container
         */
        function addUserChip(username) {
            // Remove "No users blocked" message if present
            const emptyMsg = chipsContainer.querySelector('.text-gray-500.italic');
            if (emptyMsg) {
                emptyMsg.remove();
            }
            
            // Create chip element
            const chip = document.createElement('div');
            chip.className = 'blocked-user-chip inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-sm group hover:border-red-300 transition-colors';
            chip.dataset.username = username;
            // Escape username for HTML
            const escapeHtml = (str) => {
                const div = document.createElement('div');
                div.textContent = str;
                return div.innerHTML;
            };
            
            chip.innerHTML = `
                <span class="text-gray-700">${escapeHtml(username)}</span>
                <button type="button" 
                        class="unblock-user-btn text-gray-400 hover:text-red-500 transition-colors"
                        data-username="${escapeHtml(username)}"
                        aria-label="Unblock ${escapeHtml(username)}">
                    <i class="fa fa-times text-sm"></i>
                </button>
            `;
            
            // Add with animation
            chip.style.opacity = '0';
            chip.style.transform = 'scale(0.8)';
            chipsContainer.appendChild(chip);
            
            // Trigger animation
            setTimeout(() => {
                chip.style.transition = 'all 0.3s ease-out';
                chip.style.opacity = '1';
                chip.style.transform = 'scale(1)';
            }, 10);
        }
        
        /**
         * Check if chips container is empty and show message
         */
        function checkEmptyState() {
            if (chipsContainer.children.length === 0) {
                chipsContainer.innerHTML = '<span class="text-sm text-gray-500 italic">' + (window.t ? window.t('settings.no_users_blocked') : 'No users blocked') + '</span>';
            }
        }
        
        /**
         * Update blocked users count
         */
        function updateBlockedCount(delta) {
            if (blockedCountSpan) {
                const currentCount = parseInt(blockedCountSpan.textContent) || 0;
                const newCount = Math.max(0, currentCount + delta);
                blockedCountSpan.textContent = newCount;
                
                // Update plural
                const parent = blockedCountSpan.parentElement;
                if (parent) {
                    const text = parent.textContent;
                    if (newCount === 1) {
                        parent.innerHTML = text.replace('users', 'user');
                    } else {
                        parent.innerHTML = text.replace('user.', 'users.');
                    }
                    // Restore the count span
                    const newSpan = parent.querySelector('#blocked-count');
                    if (newSpan) {
                        newSpan.textContent = newCount;
                    }
                }
            }
        }
        
        /**
         * Show status message
         */
        function showStatus(message, type = 'info') {
            if (!statusDiv) return;
            
            statusDiv.textContent = message;
            statusDiv.className = 'text-sm';
            
            if (type === 'success') {
                statusDiv.classList.add('text-green-600');
            } else if (type === 'error') {
                statusDiv.classList.add('text-red-600');
            } else {
                statusDiv.classList.add('text-gray-600');
            }
            
            statusDiv.classList.remove('hidden');
            
            // Auto-hide after 3 seconds
            setTimeout(() => {
                statusDiv.classList.add('hidden');
            }, 3000);
        }
        
        /**
         * Show error message
         */
        function showError(message) {
            if (errorDiv) {
                errorDiv.textContent = message;
                errorDiv.classList.remove('hidden');
            }
            showStatus(message, 'error');
        }
    }

    /**
     * Initialize all filter fields
     */
    function initFilterFields() {
        initContactRulesSex();
        initAgeSliders();
        initContinentChips();
        initCountrySelector();
        initBlockList();
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initFilterFields);
    } else {
        initFilterFields();
    }

})();